import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

/**
 * Статус источника выручки. Ровно три состояния, отсутствие строки в реестре
 * исключений = `ACTIVE` (docs/REVENUE_ETL_ALERTS_IMPROVEMENT_PLAN.md,
 * «Единая минимальная модель статусов»).
 *
 * ⚠️ `SERVICE` статусом НЕ является — служебный аккаунт может быть активным.
 * Это отдельная ось `CrmAccountKind`.
 */
export type RevenueSourceStatus = 'ACTIVE' | 'QUARANTINED' | 'RETIRED'

/** Подтверждённый человеком тип аккаунта. `null` = не классифицирован. */
export type CrmAccountKind = 'FRANCHISEE' | 'SERVICE'

/**
 * Наблюдаемый класс аккаунта — та же формула, что у платёжного экрана
 * (`get_revenue_account_directory`). Показывается, пока тип не подтверждён
 * человеком.
 */
export type CrmAccountObservedClass = 'franchisee' | 'no_numbered_studios' | 'unknown'

/** Вид пропажи источника по вердикту preflight. */
export type CrmSourceMissingKind = 'account' | 'account_studio'

export type CrmSourceAccountRow = {
  account: string
  city_name: string | null
  country_name: string | null
  account_fio: string | null
  directory_last_seen_on: string | null

  account_kind: CrmAccountKind | null
  account_kind_note: string | null
  account_kind_set_at: string | null
  account_kind_set_by: string | null
  observed_class: CrmAccountObservedClass

  source_status: RevenueSourceStatus
  exception_id: number | null
  exception_scope: 'account' | 'account_studio' | null
  exception_studio_number: number | null
  exception_reason: string | null
  exception_expected_return: string | null
  exception_note: string | null
  exception_review_by: string | null
  exception_review_overdue: boolean
  exception_changed_at: string | null
  exception_changed_by: string | null
  active_exception_count: number

  current_studio_numbers: number[]
  historical_studio_numbers: number[]
  partner_numbers: number[]

  live_payment_count: number
  blank_payment_count: number
  last_paid_on: string | null
  /**
   * ⚠️ Последнее ИЗМЕНЕНИЕ строк аккаунта, а не «последняя выгрузка»: merge
   * бампает `updated_at` только когда строка реально изменилась. Аккаунт со
   * стабильными данными выглядит молчащим, хотя источник исправен (замер PROD
   * 11.08.2026: 37 исправных аккаунтов без изменений >6ч). Судить об
   * отсутствии источника по этому полю нельзя — для этого есть `missing_kind`.
   */
  last_item_change_at: string | null

  missing_kind: CrmSourceMissingKind | null
  missing_studio_numbers: number[]
  missing_baseline_rows: number
  missing_abs_amount: number
  missing_detected_at: string | null
  missing_run_mode: string | null
  source_return_candidate: boolean

  frozen_row_count: number
  frozen_amounts: Record<string, number>
  open_transfer_case_count: number
}

export type CrmSourceAccountDetailSection =
  | 'studio_link'
  | 'partner_link'
  | 'transfer_case'
  | 'source_decision'

export type CrmSourceStudioLink = {
  link_id: number
  studio_internal_id: number
  studio_number: number | null
  city_name: string | null
  address: string | null
  studio_status_code: string | null
  valid_from: string
  valid_to: string | null
  is_current: boolean
  source: string
  transfer_case_id: number | null
}

export type CrmSourcePartnerLink = {
  partner_number: number | null
  persons_fio: string | null
  owner_fio_raw: string | null
  valid_from: string
  valid_to: string | null
  is_current: boolean
  source: string
}

export type CrmSourceTransferCase = {
  case_id: number
  studio_internal_id: number | null
  studio_number: number | null
  city_name: string | null
  old_account: string | null
  proposed_account: string | null
  role: 'old' | 'new'
  status: string
  transition_date: string
  applied_at: string | null
  detection_kind: string | null
}

export type CrmSourceDecision = {
  exception_id: number
  status: RevenueSourceStatus
  studio_number: number | null
  reason: string
  expected_return: string | null
  operator_note: string | null
  incident_ref: string | null
  review_by: string | null
  quarantined_at: string
  quarantined_by: string | null
  status_changed_at: string | null
  status_changed_by: string | null
  released_at: string | null
  release_kind: string | null
  release_note: string | null
  is_active: boolean
}

export type CrmSourceAccountDetail = {
  studioLinks: CrmSourceStudioLink[]
  partnerLinks: CrmSourcePartnerLink[]
  transferCases: CrmSourceTransferCase[]
  decisions: CrmSourceDecision[]
}

export type SetRevenueSourceStatusResult = {
  account: string
  studio_number: number | null
  previous_status: RevenueSourceStatus
  status: RevenueSourceStatus
  exception_id: number | null
  released_exception_id: number | null
}

export async function fetchCrmSourceAccounts(): Promise<CrmSourceAccountRow[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('list_crm_source_accounts')
  )) as { data: CrmSourceAccountRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchCrmSourceAccountDetail(
  account: string
): Promise<CrmSourceAccountDetail> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_crm_source_account_detail', { p_account: account })
  )) as {
    data: Array<{ section: CrmSourceAccountDetailSection; ordinal: number; payload: any }> | null
    error: any
  }

  if (error) throw error

  const rows = data ?? []
  const pick = <T>(section: CrmSourceAccountDetailSection): T[] =>
    rows.filter(row => row.section === section).map(row => row.payload as T)

  return {
    studioLinks: pick<CrmSourceStudioLink>('studio_link'),
    partnerLinks: pick<CrmSourcePartnerLink>('partner_link'),
    transferCases: pick<CrmSourceTransferCase>('transfer_case'),
    decisions: pick<CrmSourceDecision>('source_decision'),
  }
}

/**
 * Одно решение по источнику — один вызов. Причина обязательна на любом
 * переходе, включая возврат в работу: без неё снятие исключения было бы тихим
 * обходом защиты. Сервер сам проверяет пару «статус ↔ дата пересмотра».
 */
export async function setRevenueSourceStatus(params: {
  account: string
  status: RevenueSourceStatus
  reason: string
  studioNumber?: number | null
  reviewBy?: string | null
  expectedReturn?: string | null
  note?: string | null
  incidentRef?: string | null
}): Promise<SetRevenueSourceStatusResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_revenue_source_status', {
      p_account: params.account,
      p_status: params.status,
      p_reason: params.reason,
      p_studio_number: params.studioNumber ?? null,
      p_review_by: params.reviewBy ?? null,
      p_expected_return: params.expectedReturn ?? null,
      p_note: params.note ?? null,
      p_incident_ref: params.incidentRef ?? null,
    })
  )) as { data: SetRevenueSourceStatusResult[] | null; error: any }

  if (error) throw error
  const row = data?.[0]
  if (!row) throw new Error('Сервер не вернул результат смены статуса источника')
  return row
}

export async function setCrmAccountKind(params: {
  account: string
  kind: CrmAccountKind | null
  note?: string | null
}): Promise<{ account: string; account_kind: CrmAccountKind | null }> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('set_crm_account_kind', {
      p_account: params.account,
      p_kind: params.kind,
      p_note: params.note ?? null,
    })
  )) as { data: Array<{ account: string; account_kind: CrmAccountKind | null }> | null; error: any }

  if (error) throw error
  const row = data?.[0]
  if (!row) throw new Error('Сервер не вернул результат классификации аккаунта')
  return row
}
